/**
 * Eigene Bilder und kurze Videos zu einem Arbeitsschritt.
 *
 * Die Dateien werden sofort hochgeladen, zugeordnet werden sie aber erst mit
 * dem Speichern des Rezepts. Wird ein Medium hier entfernt und das Rezept
 * gespeichert, löscht der Server die Datei, sofern sie nirgends sonst hängt.
 */
import { useRef, useState } from 'react';
import { ApiError } from '../api/client.ts';
import type { MediaItem } from '../api/types.ts';
import { texts } from '../texts/de.ts';
import { AppIcon } from './AppIcon.tsx';

/** Höchstzahl je Schritt, wie am Server. */
const MAX_STEP_MEDIA = 6;

/** Eigenschaften der Medien eines Schritts. */
export interface StepMediaEditorProps {
  readonly media: readonly MediaItem[];
  readonly onChange: (media: MediaItem[]) => void;
}

/** Lädt eine Datei hoch, ohne sie einem Rezept zuzuordnen. */
async function uploadFile(file: File): Promise<MediaItem> {
  const form = new FormData();
  form.append('file', file);
  let response: Response;
  try {
    response = await fetch('/api/media', { method: 'POST', body: form });
  } catch {
    throw new ApiError(0, texts.editor.stepMediaOffline);
  }
  const data = (await response.json().catch(() => ({}))) as { media?: MediaItem; error?: string };
  if (!response.ok || data.media === undefined) {
    throw new ApiError(response.status, data.error ?? texts.common.error);
  }
  return data.media;
}

/** Bilder und Videos eines Schritts verwalten. */
export function StepMediaEditor({ media, onChange }: StepMediaEditorProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const full = media.length >= MAX_STEP_MEDIA;

  /** Lädt die gewählten Dateien nacheinander hoch und hängt sie an. */
  async function add(files: FileList | null) {
    if (files === null || files.length === 0) {
      return;
    }
    setBusy(true);
    setError(null);
    let next = [...media];
    try {
      for (const file of [...files].slice(0, MAX_STEP_MEDIA - next.length)) {
        const uploaded = await uploadFile(file);
        if (!next.some((item) => item.id === uploaded.id)) {
          next = [...next, uploaded];
          onChange(next);
        }
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : texts.common.error);
    } finally {
      setBusy(false);
    }
  }

  /** Verschiebt ein Medium um eine Stelle. */
  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    const a = media[index];
    const b = media[target];
    if (a === undefined || b === undefined) {
      return;
    }
    const next = [...media];
    next[index] = b;
    next[target] = a;
    onChange(next);
  }

  return (
    <details className="mt-2 rounded-lg bg-surface-sunken p-2" open={media.length > 0 ? true : undefined}>
      <summary className="cursor-pointer text-xs font-medium">
        {texts.editor.stepMedia}
        {media.length === 0 ? '' : ` (${media.length})`}
      </summary>
      <p className="mt-1 mb-2 text-xs text-content-muted">{texts.editor.stepMediaHint}</p>

      {media.length === 0 ? null : (
        <ul className="mb-2 flex flex-wrap gap-2">
          {media.map((item, index) => (
            <li
              key={item.id}
              className="relative w-28 overflow-hidden rounded-lg border border-border-subtle bg-surface"
            >
              <img src={item.thumbUrl} alt={item.originalName ?? ''} className="h-20 w-28 object-cover" />
              {item.kind === 'video' ? (
                <span className="absolute top-1 left-1 rounded bg-surface-raised/90 p-0.5">
                  <AppIcon name="video" size={16} title={texts.editor.stepMediaMove} />
                </span>
              ) : null}
              <div className="flex items-center justify-between px-1 py-0.5 text-xs">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  aria-label={`${texts.editor.stepMediaMove} ${index + 1}: ${texts.editor.moveUp}`}
                  className="rounded px-1.5 disabled:opacity-30"
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={() => onChange(media.filter((entry) => entry.id !== item.id))}
                  aria-label={`${texts.editor.stepMediaMove} ${index + 1}: ${texts.editor.stepMediaRemove}`}
                  className="rounded px-1.5 text-content-muted"
                >
                  ✕
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === media.length - 1}
                  aria-label={`${texts.editor.stepMediaMove} ${index + 1}: ${texts.editor.moveDown}`}
                  className="rounded px-1.5 disabled:opacity-30"
                >
                  →
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy || full}
          onClick={() => fileInput.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border-subtle bg-surface px-2.5 py-1.5 text-xs disabled:opacity-50"
        >
          <AppIcon name="image" size={16} />
          {texts.editor.stepMediaAdd}
        </button>
        <button
          type="button"
          disabled={busy || full}
          onClick={() => cameraInput.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border-subtle bg-surface px-2.5 py-1.5 text-xs disabled:opacity-50"
        >
          <AppIcon name="video" size={16} />
          {texts.editor.mediaCamera}
        </button>
        {busy ? <span className="text-xs text-content-muted">{texts.editor.mediaUploading}</span> : null}
      </div>
      {error === null ? null : (
        <p className="mt-2 rounded-lg bg-accent-soft px-2 py-1 text-xs text-accent-strong">{error}</p>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={(event) => {
          void add(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={cameraInput}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        className="hidden"
        onChange={(event) => {
          void add(event.target.files);
          event.target.value = '';
        }}
      />
    </details>
  );
}
